'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  FileSearch,
  Gavel,
  Home,
  Loader2,
  MapPin,
  Scale,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Zap,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import AppFooter from '@/components/AppFooter';
import LandingPlansSection from '@/components/landing/LandingPlansSection';
import ThemeToggle from '@/components/ThemeToggle';
import { useAuth } from '@/context/AuthContext';
import { isProfileComplete, resolvePostAuthRedirect } from '@/lib/profile-completion';

const stats = [
  { value: '247+', label: 'Leilões analisados' },
  { value: '22,7%', label: 'ROI médio estimado' },
  { value: '11', label: 'Sites monitorados' },
  { value: '< 2 min', label: 'Análise com IA' },
];

const features = [
  {
    id: 'score',
    icon: Target,
    title: 'Score de Oportunidade IA',
    desc: 'Ranqueamento inteligente por ROI, risco jurídico e potencial de valorização em cada imóvel.',
    accent: 'text-primary bg-primary/10 border-primary/20',
  },
  {
    id: 'legal',
    icon: Scale,
    title: 'Análise Jurídica Automatizada',
    desc: 'Ônus, débitos e alertas legais extraídos do edital — sem horas lendo documentos.',
    accent: 'text-info bg-info/10 border-info/20',
  },
  {
    id: 'financial',
    icon: TrendingUp,
    title: 'Modelagem Financeira Completa',
    desc: 'ITBI, reforma, desocupação e custos operacionais calculados automaticamente.',
    accent: 'text-success bg-success/10 border-success/20',
  },
  {
    id: 'docs',
    icon: Sparkles,
    title: 'Leitura de Edital com IA',
    desc: 'Resumo estruturado do edital completo em minutos, não em horas de leitura manual.',
    accent: 'text-accent bg-accent/10 border-accent/20',
  },
  {
    id: 'map',
    icon: MapPin,
    title: 'Mapa de Oportunidades',
    desc: 'Visualize leilões por região, filtre por score e descubra oportunidades no mapa.',
    accent: 'text-primary bg-primary/10 border-primary/20',
  },
  {
    id: 'pipeline',
    icon: BarChart3,
    title: 'Pipeline de Arrematação',
    desc: 'Gerencie favoritos, interesses e arrematações em um fluxo único de decisão.',
    accent: 'text-warning bg-warning/10 border-warning/20',
  },
  {
    id: 'arrematados',
    icon: Home,
    title: 'Gestão de Imóveis Arrematados',
    desc: 'Acompanhe status (ocupação, reforma, venda), custos pós-arrematação, lucro realizado e quadro Kanban do seu portfólio.',
    accent: 'text-success bg-success/10 border-success/20',
  },
  {
    id: 'privacy',
    icon: ShieldCheck,
    title: 'Privacidade e LGPD',
    desc: 'Cada usuário é dono da própria análise. Seus dados, favoritos, anotações e simulações ficam isolados — conforme a Lei Geral de Proteção de Dados.',
    accent: 'text-info bg-info/10 border-info/20',
  },
];

const steps = [
  {
    id: 'explore',
    step: '01',
    title: 'Explore o catálogo',
    desc: 'Monitore dezenas de leiloeiros em um só lugar. Filtros avançados por tipo, região e score.',
  },
  {
    id: 'analyze',
    step: '02',
    title: 'Analise com IA',
    desc: 'Score, viabilidade financeira e riscos jurídicos gerados automaticamente para cada imóvel.',
  },
  {
    id: 'decide',
    step: '03',
    title: 'Decida e gerencie',
    desc: 'Compare oportunidades, simule lances, registre arrematações e administre cada imóvel até a venda.',
  },
];

const benefits = [
  'Monitoramento automático de novos leilões',
  'Alertas de oportunidades com alto score',
  'Calculadora de ROI e lance ideal',
  'Gestão completa de imóveis arrematados',
  'Análises privadas — só você acessa seus dados',
  '14 dias grátis para começar',
];

function LoginCta({
  className = '',
  size = 'default',
}: {
  className?: string;
  size?: 'default' | 'lg';
}) {
  const base =
    size === 'lg'
      ? 'inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-bold text-primary-foreground shadow-elevated transition-all hover:bg-primary/90 active:scale-[0.98]'
      : 'btn-primary inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm';

  return (
    <Link href="/login" className={`${base} ${className}`.trim()}>
      Entrar na plataforma
      <ArrowRight size={size === 'lg' ? 18 : 16} aria-hidden />
    </Link>
  );
}

export default function LandingPage() {
  const router = useRouter();
  const { isAuthenticated, isLoading, user } = useAuth();

  useEffect(() => {
    if (!isLoading && isAuthenticated && user && isProfileComplete(user)) {
      void resolvePostAuthRedirect(user).then((path) => router.replace(path));
    }
  }, [isAuthenticated, isLoading, user, router]);

  if (isLoading || (isAuthenticated && user && isProfileComplete(user))) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/80 bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4 sm:h-16 sm:px-6">
          <Link href="/" className="flex min-w-0 items-center gap-2.5">
            <AppLogo size={32} />
            <span className="truncate text-base font-extrabold tracking-tight text-foreground sm:text-lg">
              Metrica Leilões
            </span>
          </Link>

          <nav className="hidden items-center gap-6 md:flex" aria-label="Navegação principal">
            <a
              href="#recursos"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Recursos
            </a>
            <a
              href="#como-funciona"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Como funciona
            </a>
            <a
              href="#planos"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Planos
            </a>
            <a
              href="#beneficios"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Benefícios
            </a>
            <a
              href="#privacidade"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Privacidade
            </a>
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <ThemeToggle compact />
            <LoginCta />
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-primary/8 blur-3xl" />
            <div className="absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-accent/8 blur-3xl" />
            <div className="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/5 blur-2xl" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:py-28">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5">
                <Gavel size={14} className="text-accent" aria-hidden />
                <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                  Plataforma de Análise de Leilões com IA
                </span>
              </div>

              <h1 className="text-3xl font-extrabold leading-[1.12] tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                Encontre as melhores oportunidades de leilão{' '}
                <span className="text-primary">antes dos outros</span>
              </h1>

              <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                Monitoramento automático, score inteligente, análise jurídica e modelagem financeira
                — tudo em uma plataforma feita para investidores que querem decidir com dados, não
                achismos.
              </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                <LoginCta size="lg" />
                <a
                  href="#recursos"
                  className="btn-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base"
                >
                  Ver recursos
                </a>
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Sem cartão de crédito · 14 dias grátis · Cancele quando quiser
              </p>
            </div>

            {/* Stats */}
            <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
              {stats.map(({ value, label }) => (
                <div
                  key={label}
                  className="card flex flex-col items-center px-4 py-5 text-center shadow-sm transition-shadow hover:shadow-elevated"
                >
                  <span className="font-mono-nums text-2xl font-extrabold text-primary sm:text-3xl">
                    {value}
                  </span>
                  <span className="mt-1 text-xs font-medium text-muted-foreground sm:text-sm">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product preview */}
        <section className="border-y border-border bg-muted/30 py-14 sm:py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
              <div>
                <span className="badge border-primary/20 bg-primary/10 text-primary">
                  Visão geral
                </span>
                <h2 className="mt-4 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  Do edital ao lance ideal em uma única tela
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Pare de alternar entre planilhas, PDFs e sites de leiloeiros. Metrica Leilões
                  centraliza descoberta, análise e decisão — com dados atualizados e score de
                  oportunidade em tempo real.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Score IA por imóvel',
                    'Waterfall de custos e ROI',
                    'Mapa interativo de oportunidades',
                    'Portfólio de arrematados com Kanban',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2.5 text-sm text-foreground">
                      <CheckCircle2 size={18} className="shrink-0 text-success" aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative">
                <div className="card overflow-hidden shadow-elevated">
                  <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-3">
                    <div className="flex gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-danger/60" />
                      <span className="h-2.5 w-2.5 rounded-full bg-warning/60" />
                      <span className="h-2.5 w-2.5 rounded-full bg-success/60" />
                    </div>
                    <span className="ml-2 text-xs font-medium text-muted-foreground">
                      Oportunidades · São Paulo
                    </span>
                  </div>
                  <div className="space-y-3 p-4">
                    {[
                      {
                        tier: 'tier-excelente',
                        score: '92',
                        addr: 'Apto 85m² · Vila Mariana',
                        roi: '28,4%',
                      },
                      { tier: 'tier-bom', score: '78', addr: 'Casa 120m² · Moema', roi: '19,2%' },
                      {
                        tier: 'tier-regular',
                        score: '64',
                        addr: 'Sala 45m² · Pinheiros',
                        roi: '14,1%',
                      },
                    ].map(({ tier, score, addr, roi }) => (
                      <div
                        key={addr}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-foreground">{addr}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            ROI estimado · {roi}
                          </p>
                        </div>
                        <span className={`badge shrink-0 ${tier}`}>
                          <Zap size={10} aria-hidden />
                          {score}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 hidden rounded-xl border border-border bg-card p-3 shadow-elevated sm:block">
                  <div className="flex items-center gap-2">
                    <FileSearch size={16} className="text-primary" aria-hidden />
                    <span className="text-xs font-semibold text-foreground">
                      Edital analisado em 1m 42s
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="recursos" className="py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <span className="badge border-accent/30 bg-accent/10 text-accent">Recursos</span>
              <h2 className="mt-4 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Tudo que você precisa para investir em leilões
              </h2>
              <p className="mt-3 text-sm text-muted-foreground sm:text-base">
                Da descoberta à arrematação e gestão do portfólio — com seus dados protegidos e
                isolados por usuário.
              </p>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
              {features.map(({ id, icon: Icon, title, desc, accent }) => (
                <article
                  key={id}
                  className="card group p-6 transition-all hover:border-primary/30 hover:shadow-elevated"
                >
                  <div
                    className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border ${accent}`}
                  >
                    <Icon size={20} aria-hidden />
                  </div>
                  <h3 className="text-base font-bold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                </article>
              ))}
            </div>

            {/* LGPD highlight */}
            <div
              id="privacidade"
              className="card mt-10 flex flex-col gap-6 border-info/25 bg-info/5 p-6 sm:flex-row sm:items-start sm:p-8"
            >
              <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-info/20 bg-info/10 text-info">
                <ShieldCheck size={24} aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold text-foreground">Seus dados, suas análises</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Na Metrica Leilões, cada conta é um ambiente privado. Suas simulações financeiras,
                  observações, favoritos, interesses e histórico de arrematações pertencem
                  exclusivamente a você — nunca são compartilhados com outros usuários. Tratamos
                  dados pessoais em conformidade com a{' '}
                  <strong className="font-semibold text-foreground">
                    LGPD (Lei nº 13.709/2018)
                  </strong>
                  , com controle de acesso, isolamento por usuário e transparência sobre o uso das
                  informações.
                </p>
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                  {[
                    'Análises e anotações visíveis só para você',
                    'Dados de perfil protegidos e criptografados em trânsito',
                    'Sem venda de dados a terceiros',
                    'Direitos do titular: acesso, correção e exclusão',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-info" aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <LandingPlansSection />

        {/* How it works */}
        <section id="como-funciona" className="border-y border-border bg-muted/20 py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <span className="badge border-primary/20 bg-primary/10 text-primary">
                Como funciona
              </span>
              <h2 className="mt-4 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Três passos para sua próxima arrematação
              </h2>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {steps.map(({ id, step, title, desc }) => (
                <div key={id} className="relative card p-6">
                  <span className="font-mono-nums text-3xl font-extrabold text-primary/20">
                    {step}
                  </span>
                  <h3 className="mt-2 text-lg font-bold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section id="beneficios" className="py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="card overflow-hidden lg:flex">
              <div className="flex flex-1 flex-col justify-center p-8 sm:p-10 lg:p-12">
                <span className="badge w-fit border-success/20 bg-success/10 text-success">
                  Para investidores
                </span>
                <h2 className="mt-4 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  Ganhe vantagem competitiva em cada leilão
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Invista menos tempo pesquisando e mais tempo decidindo. Metrica Leilões foi
                  construída para quem trata leilão como estratégia — não como aposta.
                </p>
                <ul className="mt-8 space-y-3">
                  {benefits.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-foreground">
                      <CheckCircle2
                        size={18}
                        className="mt-0.5 shrink-0 text-success"
                        aria-hidden
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col justify-center bg-primary p-8 text-primary-foreground sm:p-10 lg:w-[380px] lg:shrink-0 lg:p-12">
                <div className="flex items-center gap-2">
                  {['RM', 'AC', 'FS', 'BT'].map((initials, i) => (
                    <div
                      key={initials}
                      className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-primary bg-white/15"
                      style={{ marginLeft: i > 0 ? '-8px' : 0, zIndex: 4 - i }}
                    >
                      <span className="text-[10px] font-bold">{initials}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-sm font-semibold">+1.200 investidores ativos</p>
                <div className="mt-1 flex items-center gap-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={`star-${i + 1}`} className="text-accent text-sm">
                      ★
                    </span>
                  ))}
                  <span className="ml-1 text-xs text-white/70">4,8/5 de satisfação</span>
                </div>
                <blockquote className="mt-6 border-l-2 border-white/30 pl-4 text-sm leading-relaxed text-white/90">
                  &ldquo;Reduzi de 4 horas para 20 minutos o tempo de analisar um edital. O score IA
                  mudou completamente minha forma de escolher lances.&rdquo;
                </blockquote>
                <p className="mt-3 text-xs text-white/60">— Investidor imobiliário, São Paulo</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative overflow-hidden py-16 sm:py-20">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary via-primary to-blue-800" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.12),transparent_50%)]" />

          <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
            <Sparkles className="mx-auto text-accent" size={32} aria-hidden />
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Pronto para encontrar sua próxima oportunidade?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base">
              Crie sua conta, complete seu perfil e comece a analisar leilões com IA hoje mesmo.
              Seus primeiros 14 dias são por nossa conta.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                href="/login"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-3.5 text-base font-bold text-primary shadow-elevated transition-all hover:bg-white/95 active:scale-[0.98]"
              >
                Começar agora — é grátis
                <ArrowRight size={18} aria-hidden />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <AppFooter />
    </div>
  );
}
